/**
 * SimplyLoan Meta Ads API client.
 *
 * GET /api/simplyloan/meta-dashboard returns cohort revenue: every number on a row belongs to the
 * leads that row acquired in the selected dates, whenever the commission was earned. Money is DKK.
 * The Cognito token is attached by ../authFetch, like every other dashboard call.
 */
import { API_BASE_URL } from '../config';

export type MetaGeo = 'all' | 'se' | 'no' | 'fi';
export type MetaLevel = 'campaign' | 'adset' | 'ad';

export interface MetaDashboardParams {
  geo: MetaGeo;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  level: MetaLevel;
}

export interface MetaDashboardRow {
  key: string | null;               // campaign_id, adset_id or ad_id; null = Meta leads without an ad id
  name: string;
  campaign_name: string | null;
  adset_name: string | null;
  geo: string | null;
  status: string | null;            // Meta effective_status
  spend_dkk: number;
  fx_missing: boolean;
  impressions: number;
  link_clicks: number;
  meta_leads: number;               // as Meta reports them
  leads: number;                    // our leads (web + Instant Form)
  lead_ad_leads: number;
  rev_d0: number;                   // approved commission within 24 h of acquisition
  rev_d7: number;
  rev_d14: number;
  rev_d30: number;
  rev_d90: number;                  // to date, capped at 90 days
  rev_pending: number;
  leads_mature_d30: number;
  projected_d30: number | null;
  cpl_dkk: number | null;
  drip_revenue_dkk: number;
  immediate_roas: number | null;
  ltv30_roas: number | null;
  projected_ltv30_roas: number | null;
  roas_to_date: number | null;
}

export interface MetaDashboardKpis {
  spend_dkk: number;
  leads: number;
  lead_ad_leads: number;
  meta_reported_leads: number;
  cpl_dkk: number | null;
  frontend_revenue_dkk: number;
  drip_revenue_dkk: number;
  total_revenue_dkk: number;
  pending_revenue_dkk: number;
  blended_roas: number | null;
  net_margin_dkk: number;
  projected_d30_revenue_dkk: number | null;
  unmapped_spend_dkk: number;
  fx_missing: boolean;
}

export interface CapiHealth {
  hours: number;
  by_status: Record<string, number>;
  by_event: Record<string, Record<string, number>>;
  attempted: number;
  error_rate: number | null;
  strong_match_share: number | null;
  auth_errors: number;
  backlog_over_60m: number;
}

export interface MetaDashboardResponse {
  filters: { geo: string; level: MetaLevel; from: string; to: string; timezone: string };
  currency: 'DKK';
  kpis: MetaDashboardKpis;
  rows: MetaDashboardRow[];
  d30_multipliers: Record<string, number>;
  health: { capi_24h: CapiHealth; synced_at: Record<string, string | null> };
  generated_at: string;
}

export async function fetchMetaDashboard(
  params: MetaDashboardParams,
  signal?: AbortSignal,
): Promise<MetaDashboardResponse> {
  const qs = new URLSearchParams({ geo: params.geo, from: params.from, to: params.to, level: params.level });
  const res = await fetch(`${API_BASE_URL}/api/simplyloan/meta-dashboard?${qs.toString()}`, { signal });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Meta dashboard request failed (${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
  }
  return res.json();
}

// ─── Campaign builder ────────────────────────────────────────────────────────
// Plans become PAUSED campaigns in Meta; activation is a separate, approved step.

export type PlanStatus =
  | 'draft' | 'validated' | 'queued' | 'building' | 'dry_run' | 'built' | 'failed' | 'active' | 'paused';

export interface PlanFinding {
  level: 'error' | 'warning';
  field: string;
  message: string;
  match: string;
}

export interface PlanPreviewAd {
  ad_set: string;
  ad: string;
  headline: string;
  description: string;
  primary_text: string;   // exactly as it will be sent, legal text included
  cta: string;
  landing_url: string | null;
}

export interface PlanValidation {
  ok: boolean;
  errors: PlanFinding[];
  warnings: PlanFinding[];
  preview: PlanPreviewAd[];
  daily_budget: number | null;
  currency: string | null;
}

export interface CampaignPlanRow {
  id: number;
  plan_key: string;
  geo: string;
  name: string;
  status: PlanStatus;
  validation: { ok: boolean; errors: PlanFinding[]; warnings: PlanFinding[]; daily_budget: number | null; currency: string | null } | null;
  last_error: string | null;
  created_by: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
  built_at: string | null;
  activated_at: string | null;
  objects: number;
  campaign_id: string | null;
  spec: Record<string, unknown>;
}

export interface PlanListResponse {
  mode: 'dry_run' | 'live';
  activation_allowed: boolean;
  plans: CampaignPlanRow[];
}

export interface PlanTemplate {
  file: string;
  spec: Record<string, unknown>;
}

export interface PlanActionResponse extends Partial<PlanValidation> {
  status: string;
  plan_id?: number;
  reason?: string;
  activated?: number;
  paused?: number;
}

async function campaignsRequest<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = body === undefined
    ? {}
    : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
  const res = await fetch(`${API_BASE_URL}/api/simplyloan/meta-campaigns${path}`, init);
  const text = await res.text();
  let parsed: any = {};
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = { error: text.slice(0, 200) };
  }
  // 409 carries a refusal with its reason (e.g. "activation is off"); the caller shows it.
  if (!res.ok && res.status !== 409) {
    throw new Error(parsed?.error || parsed?.reason || `Request failed (${res.status})`);
  }
  return parsed as T;
}

export const listCampaignPlans = () => campaignsRequest<PlanListResponse>('');
export const getCampaignTemplates = () => campaignsRequest<{ templates: PlanTemplate[] }>('/templates');
export const validateCampaignPlan = (plan: unknown) => campaignsRequest<PlanValidation>('/validate', { plan });
export const saveCampaignPlan = (plan: unknown) => campaignsRequest<PlanActionResponse>('', { plan });
export const campaignPlanAction = (action: 'queue' | 'activate' | 'pause', planId: number) =>
  campaignsRequest<PlanActionResponse>(`/${action}`, { plan_id: planId });
